import { ReactNode, Ref } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { CircleCheck, RotateCcwIcon } from 'components/icons';
import { useFormatDate } from 'components/misc/FormattedDate';
import { Avatar, AvatarFallback } from 'components/ui/avatar';
import { Button } from 'components/ui/button';
import type { InvoiceComment } from 'types/invoiceComments';
import { cn } from 'utils/cn';
import { getInitials } from 'utils/nameOrder';
import { tagSegments } from './commentTree';

/** A tag keeps its highlight whether or not it links to the person, in a saved entry as in the composer. */
export const TAG_CLASS =
    'new:bg-primary-tint new:text-primary-ink bg-primary/10 text-primary inline-block max-w-full rounded-sm px-0.5 font-medium';

/** The indent stops at three steps; a deeper reply says whom it answers instead. */
export const MAX_INDENT = 3;

export type EntryActions = {
    onReply?: () => void;
    onEdit?: () => void;
    onDelete?: () => void;
    /** Mark as handled or Reopen, on a top-level comment only. */
    onToggleHandled?: () => void;
};

type Props = {
    comment: InvoiceComment;
    depth: number;
    /** The author of the entry a reply answers. */
    repliedToName?: string;
    /** On a handled card the text takes the secondary ink. */
    handledCard: boolean;
    actions: EntryActions;
    /** The composer that replaces the text while the entry is edited. */
    editor?: ReactNode;
    groupRef?: Ref<HTMLDivElement>;
};

/**
 * One comment or reply: the author's avatar and name, when it was written, the text with its tags, whom it was
 * edited by, and the actions. A group named by its author and time, so its buttons are heard with the entry.
 */
export function InvoiceCommentEntry({ comment, depth, repliedToName, handledCard, actions, editor, groupRef }: Props) {
    const intl = useIntl();
    const formatDate = useFormatDate();

    if (comment.deleted) {
        return (
            <div className="grid grid-cols-[24px_minmax(0,1fr)] items-center gap-x-2">
                <span aria-hidden="true" className="border-divider size-6 rounded-full border border-dashed" />
                <p className="text-fg-secondary m-0 text-sm italic">
                    <FormattedMessage id="invoice-comments.entry-deleted" />
                </p>
            </div>
        );
    }

    const unknown = intl.formatMessage({ id: 'unknown' });
    const authorName = comment.author?.name ?? unknown;
    const dateTime = formatDate(comment.createdAt, true) ?? '';
    const isTopLevel = comment.parentId === null;
    const replyTo = repliedToName ? (
        <FormattedMessage id="invoice-comments.reply-to" values={{ name: repliedToName }} />
    ) : null;

    return (
        <div
            ref={groupRef}
            role="group"
            aria-label={intl.formatMessage({ id: 'invoice-comments.entry-label' }, { name: authorName, dateTime })}
            // The focus is sent here after a save, a delete, a card hidden as handled and a notification's arrival.
            tabIndex={-1}
            data-comment-group={comment.id}
            className="outline-none"
        >
            <div className="grid grid-cols-[24px_minmax(0,1fr)] gap-x-2">
                <Avatar size="sm">
                    <AvatarFallback>{getInitials(authorName)}</AvatarFallback>
                </Avatar>
                <div className="flex min-h-6 flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="text-fg text-sm font-medium">{authorName}</span>
                    <span className="text-fg-secondary text-xs">{dateTime}</span>
                    {isTopLevel && actions.onToggleHandled ? (
                        comment.handledAt ? (
                            <Button
                                type="button"
                                variant="ghost"
                                size="xs"
                                className="ms-auto"
                                onClick={actions.onToggleHandled}
                            >
                                <RotateCcwIcon data-icon="inline-start" className="size-3.5" />
                                <FormattedMessage id="invoice-comments.reopen" />
                            </Button>
                        ) : (
                            <Button
                                type="button"
                                variant="outline"
                                size="xs"
                                className="ms-auto"
                                onClick={actions.onToggleHandled}
                            >
                                <CircleCheck data-icon="inline-start" className="size-3.5" />
                                <FormattedMessage id="invoice-comments.mark-handled" />
                            </Button>
                        )
                    ) : null}
                </div>
                <div className="col-start-2 min-w-0">
                    {replyTo && depth > MAX_INDENT ? (
                        <p className="text-fg-secondary m-0 mt-0.5 text-xs">{replyTo}</p>
                    ) : null}
                    {replyTo && depth <= MAX_INDENT ? <span className="sr-only">{replyTo}</span> : null}
                    {isTopLevel && comment.handledAt ? (
                        <p className="text-success-darker new:text-success-ink m-0 mt-0.5 flex items-start gap-1 text-xs">
                            <CircleCheck aria-hidden="true" className="mt-px size-3.5 shrink-0" />
                            <FormattedMessage
                                id="invoice-comments.handled-by"
                                values={{
                                    // A handler named by no employee, such as `system`, reads as Unknown.
                                    name: comment.handledBy?.id != null ? comment.handledBy.name : unknown,
                                    date: formatDate(comment.handledAt) ?? '',
                                }}
                            />
                        </p>
                    ) : null}
                    {editor ?? (
                        <p
                            className={cn(
                                'm-0 mt-0.5 text-sm leading-[1.5] wrap-break-word whitespace-pre-line',
                                handledCard ? 'new:text-fg-secondary text-grey-600' : 'text-fg'
                            )}
                        >
                            {tagSegments(comment.text ?? '', comment.mentions).map((segment, index) =>
                                !segment.tag ? (
                                    segment.text
                                ) : segment.employeeId === null ? (
                                    <span key={index} className={TAG_CLASS}>
                                        {segment.text}
                                    </span>
                                ) : (
                                    // A plain record link, so the page's `LinkPeekHost` hangs the employee's peek card off it.
                                    <a
                                        key={index}
                                        href={`/employee/${segment.employeeId}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={cn(
                                            TAG_CLASS,
                                            'no-underline hover:underline data-peek-open:underline'
                                        )}
                                    >
                                        {segment.text}
                                    </a>
                                )
                            )}
                        </p>
                    )}
                    {!editor && comment.lastModifiedAt ? (
                        <p className="text-fg-secondary m-0 mt-1 text-xs">
                            <FormattedMessage
                                id="invoice-comments.edited-by"
                                values={{
                                    name: comment.lastModifiedBy?.name ?? unknown,
                                    dateTime: formatDate(comment.lastModifiedAt, true) ?? '',
                                }}
                            />
                        </p>
                    ) : null}
                    {!editor && (actions.onReply || actions.onEdit || actions.onDelete) ? (
                        <div className="-ms-2 mt-1 flex flex-wrap gap-1">
                            {actions.onReply ? (
                                <Button type="button" variant="ghost" size="xs" onClick={actions.onReply}>
                                    <FormattedMessage id="invoice-comments.reply" />
                                </Button>
                            ) : null}
                            {actions.onEdit ? (
                                <Button type="button" variant="ghost" size="xs" onClick={actions.onEdit}>
                                    <FormattedMessage id="edit" />
                                </Button>
                            ) : null}
                            {actions.onDelete ? (
                                <Button type="button" variant="ghost" size="xs" onClick={actions.onDelete}>
                                    <FormattedMessage id="delete" />
                                </Button>
                            ) : null}
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
}
